import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { formatIDR } from "../lib/format";

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const merchantRef = searchParams.get("merchant_ref") || searchParams.get("tripay_merchant_ref");
  const [status, setStatus] = useState("polling"); // polling, paid, failed, expired
  const [info, setInfo] = useState(null);
  const { clear } = useCart();
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!merchantRef) {
      setStatus("failed");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attemptsRef.current >= 20) {
        // After 20 attempts (~60s), let user keep waiting manually
        return;
      }
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${merchantRef}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          clear();
          return;
        }
        if (data.payment_status === "expired" || data.payment_status === "failed") {
          setStatus(data.payment_status);
          return;
        }
        setTimeout(poll, 3000);
      } catch {
        setTimeout(poll, 3000);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantRef]);

  return (
    <div data-testid="checkout-success-page" className="max-w-2xl mx-auto px-4 md:px-8 py-24 text-center">
      {status === "polling" && (
        <>
          <Loader2 className="w-12 h-12 text-[#00F0FF] mx-auto animate-spin" />
          <h1 className="font-display font-black text-3xl text-white mt-6">Memverifikasi pembayaran...</h1>
          <p className="text-white/60 mt-2">
            {info?.checkout_url ? (
              <>
                Belum selesai bayar?{" "}
                <a href={info.checkout_url} className="text-[#00F0FF] hover:underline">
                  Lanjutkan di Tripay
                </a>
              </>
            ) : (
              "Sedang konfirmasi status ke Tripay."
            )}
          </p>
          {merchantRef && (
            <p className="text-[10px] uppercase tracking-widest text-white/30 mt-4">Ref: {merchantRef}</p>
          )}
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-16 h-16 text-[#00FF66] mx-auto" />
          <h1 className="font-display font-black text-4xl text-white mt-6">Pembayaran Berhasil</h1>
          <p className="text-white/60 mt-2">
            {formatIDR(info?.amount_idr || 0)} — Kode voucher/akun tersedia di detail pesanan kamu.
          </p>
          <div className="mt-8 flex gap-3 justify-center flex-wrap">
            {info?.order_id ? (
              <Link to={`/orders/${info.order_id}`} data-testid="view-order-detail-btn" className="btn-primary">Lihat Detail Pesanan</Link>
            ) : (
              <Link to="/account" data-testid="view-orders-btn" className="btn-primary">Lihat Pesanan</Link>
            )}
            <Link to="/browse" className="btn-ghost">Belanja Lagi</Link>
          </div>
        </>
      )}
      {(status === "failed" || status === "expired") && (
        <>
          <XCircle className="w-16 h-16 text-[#FF003C] mx-auto" />
          <h1 className="font-display font-black text-3xl text-white mt-6">
            {status === "expired" ? "Sesi pembayaran kadaluarsa" : "Pembayaran gagal"}
          </h1>
          <p className="text-white/60 mt-2">Silakan coba lagi atau hubungi support.</p>
          <Link to="/cart" className="btn-primary inline-block mt-6">Kembali ke Keranjang</Link>
        </>
      )}
    </div>
  );
}
